import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { User } from "../interfaces/entities/user";
import { Collection } from "../enums/collections.enum";
import { readEntity, readFilteredEntity, readAllEntity, createEntity, updateEntity, deleteEntity } from "../services/crudService";
import { deleteStorageFile, getUserImagePath } from "../services/storageService";
import { UserRoles } from "../enums/userRoles.enum";
import { Review } from "../interfaces/entities/review";
import { isOwnerOfResource } from "../hooks/useAuthenticatedUser";


export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [Collection.Users, Collection.Reviews],
  endpoints: (build) => ({
    getUserById: build.query<User | null, string>({
      async queryFn(id: string) {
        try {
          if (!id) return { data: null };
          const user = await readEntity<User>(Collection.Users, id);
          return user ? { data: user } : { error: { status: 404, message: "User not found" } };
        } catch (error) {
          return { error: { status: 500, message: (error as Error).message } };
        }
      },
      providesTags: (result, error, id) => (result ? [{ type: Collection.Users, id }] : []),
    }),

    // Check if email already exists
    checkEmailExists: build.query<boolean, string>({
      async queryFn(email: string) {
        try {
          const users = await readFilteredEntity<User>(Collection.Users, [
            { field: "email", operator: "==", value: email },
          ]);
          return { data: users.length > 0 };
        } catch (error) {
          return { error: { status: 500, message: (error as Error).message } };
        }
      },
      providesTags: (result, error, email) => [{ type: Collection.Users, id: `email-${email}` }],
    }),

    getUsersByBranchId: build.query<User[], { branchId: string | undefined }>({
      async queryFn(filters) {
        try {
          const users = await readFilteredEntity<User>(Collection.Users, [
            { field: "branchId", operator: "==", value: filters.branchId },
            { field: "role", operator: "==", value: UserRoles.employee },
          ]);
          return { data: users };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              message: error.message || "Failed to fetch users by branch ID.",
            },
          };
        }
      },
      providesTags: [{ type: Collection.Users, id: "LIST" }],
    }),

    getComapanyOwners: build.query<User[], { companyId: string | undefined }>({
      async queryFn(filters) {
        try {
          const users = await readFilteredEntity<User>(Collection.Users, [
            { field: "companyId", operator: "==", value: filters.companyId },
            { field: "role", operator: "==", value: UserRoles.owner },
          ]);
          return { data: users };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              message: error.message || "Failed to fetch users by branch ID.",
            },
          };
        }
      },
      providesTags: [{ type: Collection.Users, id: "LIST" }],
    }),

    getAllUsers: build.query<User[], void>({
      async queryFn() {
        try {
          const users = await readAllEntity<User>(Collection.Users);
          return { data: users };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              message: error.message || "Failed to fetch all users.",
            },
          };
        }
      },
      providesTags: [{ type: Collection.Users, id: "LIST" }],
    }),

    createUser: build.mutation<User, { user: User }>({
      async queryFn({ user }, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;

        try {
          // Check if email already exists
          const existingUsers = await readFilteredEntity<User>(Collection.Users, [
            { field: "email", operator: "==", value: user.email },
          ]);

          if (existingUsers.length > 0) {
            throw new Error("A user with this email already exists");
          }

          if (isOwnerOfResource(user.companyId, loggedInUser) || loggedInUser?.role === UserRoles.admin) {
            // Set ownerId to the logged-in user's id
            const createdUser = await createEntity<User>(
              Collection.Users,
              user
            );
            return { data: createdUser };
          } else {
            throw new Error("This company not belongs to you");
          }
        } catch (error: any) {
          return {
            error: {
              status: 500,
              message: error.message || "Failed to create user.",
            },
          };
        }
      },
      invalidatesTags: [{ type: Collection.Users, id: "LIST" }],
    }),

    updateUser: build.mutation<Partial<User>, { id: string; data: Partial<User> }>({
      async queryFn({ id, data }, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;
        const editableUser = await readEntity<User>(Collection.Users, id);

        if (editableUser && (isOwnerOfResource(editableUser.companyId, loggedInUser) || loggedInUser?.role === UserRoles.admin)) {

          // Check if email already exists (excluding current user)
          if (data.email) {
            const existingUsers = await readFilteredEntity<User>(Collection.Users, [
              { field: "email", operator: "==", value: data.email },
            ]);

            // If there are other users with this email (not the current user being edited)
            const otherUserWithEmail = existingUsers.find(u => u.id !== id);
            if (otherUserWithEmail) {
              return {
                error: { status: 400, message: "A user with this email already exists" },
              };
            }
          }

          try {
            await updateEntity<User>(Collection.Users, id, data);
            return { data: data as Partial<User> };

          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        } else {
          return {
            error: { status: 403, message: 'You do not have permission to update this user' },
          };
        }
      },
      invalidatesTags: (result, error, { id }) =>
        result ? [{ type: Collection.Users, id }, { type: Collection.Users, id: "LIST" }] : [{ type: Collection.Users, id: "LIST" }],
    }),

    deleteEmployeeWithDependancies: build.mutation<void, { userId: string }>({
      async queryFn({ userId }, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;
        const deletableUser = await readEntity<User>(Collection.Users, userId);

        if (!deletableUser) {
          return {
            error: { status: 403, message: 'User not found in db' },
          };
        } else if (loggedInUser?.role === UserRoles.admin || (isOwnerOfResource(deletableUser.companyId, loggedInUser))) {
          try {
            // Step 1: Delete image of the user
            await deleteStorageFile(getUserImagePath(deletableUser.companyId, userId));

            // Step 2: Delete all reviews for this user
            const reviews = await readFilteredEntity<Review>(Collection.Reviews, [
              { field: "userId", operator: "==", value: userId },
            ]);

            if (reviews.length > 0) {
              const deleteReviewPromises = reviews.map(review =>
                deleteEntity(Collection.Reviews, review.id)
              );
              await Promise.all(deleteReviewPromises);
            }

            // Step 3: Delete the user object
            await deleteEntity(Collection.Users, userId);

            return { data: undefined };

          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        } else {
          return {
            error: { status: 403, message: 'You do not have permission to update this user' },
          };
        }
      },
      invalidatesTags: [{ type: Collection.Users, id: "LIST" }, { type: Collection.Reviews }],
    }),
  }),
});

export const { useGetUserByIdQuery, useCheckEmailExistsQuery, useGetUsersByBranchIdQuery, useGetComapanyOwnersQuery, useGetAllUsersQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteEmployeeWithDependanciesMutation } = userApi;

